import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { paletteStyle } from "../lib/theme";
import { useHbdTheme } from "../lib/themeContext";
import { LangToggle } from "./LangToggle";
import { Cake, MapPin, Ticket } from "./Icon";
import { CAFE_NAME, MAPS_URL, EVENT_TITLE_EN, COMPANY } from "../../shared/event";

function Header() {
  const { t } = useI18n();
  const links = [
    { href: "/#schedule", label: t("nav.schedule") },
    { href: "/#giveaways", label: t("nav.giveaways") },
    { href: "/#faq", label: t("nav.faq") },
    { href: "/#location", label: t("nav.location") },
  ];
  return (
    <header className="sticky top-0 z-30 border-b border-black/[0.06] bg-cream/85 backdrop-blur">
      <div className="container-x flex items-center gap-3 py-3.5">
        <Link to="/" className="flex items-center gap-2 font-display text-lg font-bold text-[var(--theme-deep)]">
          <Cake className="h-6 w-6" strokeWidth={2.2} />
          <span className="hidden sm:inline">HBD Bogummy</span>
        </Link>
        <nav className="ml-2 hidden flex-1 items-center gap-6 text-[15px] font-semibold text-ink/70 md:flex">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="transition hover:text-[var(--theme-deep)]">
              {l.label}
            </a>
          ))}
          <Link to="/wall" className="transition hover:text-[var(--theme-deep)]">
            {t("nav.wall")}
          </Link>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <LangToggle />
          <Link to="/reserve" className="btn-primary px-4 py-2 text-sm">
            <Ticket className="h-4 w-4" />
            {t("nav.reserve")}
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="mt-20 border-t border-black/[0.06] bg-white/60">
      <div className="container-x flex flex-col items-center gap-3 py-10 text-center text-[15px] text-ink/60">
        <div className="font-display text-base font-bold text-[var(--theme-deep)]">{EVENT_TITLE_EN}</div>
        <a
          href={MAPS_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 transition hover:text-[var(--theme-deep)]"
        >
          <MapPin className="h-4 w-4" /> {CAFE_NAME}
        </a>
        <div className="flex gap-5">
          <Link to="/wall" className="hover:text-[var(--theme-deep)]">{t("nav.wall")}</Link>
          <Link to="/reserve" className="hover:text-[var(--theme-deep)]">{t("nav.reserve")}</Link>
        </div>
        <div className="mt-2 text-sm text-ink/45">
          {t("footer.unofficial")} ·{" "}
          <span>
            {t("footer.developedBy")}{" "}
            <a
              href={COMPANY.url}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink/70 hover:text-[var(--theme-deep)]"
            >
              {COMPANY.name}
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { theme } = useHbdTheme();
  return (
    <div style={paletteStyle(theme)} className="theme-wash flex min-h-dvh flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
